import * as Api from "../../Api";
import * as UserSelectors from "../User/User.selector";
import * as InsightsSelectors from "./Insights.selector";
import * as StorageUtils from "../../lib/util/StorageUtils";

import { actionCreator, asyncActionCreators } from "../../lib/util/ReduxUtils";
import { Insight } from "../../lib/types/InsightTypes";
import { ScopeInfo } from "../../lib/types/MiscTypes";
import { DEFAULT_LOCALE } from "../../lib/util/LocaleUtils";
import { CURRENT_LOCALE_KEY } from "../../StorageKeys";

export const resetSelections = actionCreator<void>(
	"[INSIGHTS] RESET_SELECTIONS",
);

export const setNavigationSelection = actionCreator<string>(
	"[INSIGHTS] SET_NAVIGATION_SELECTION",
);

export const setFilterSelection = actionCreator<{
	filterType: string;
	value: string;
}>("[INSIGHTS] SET_FILTER_SELECTION");

export interface UpdateInsightData {
	id: string;
	data: Partial<Insight>;
	survey?: any;
	onSuccess?: () => void;
	onError?: () => void;
}

export const updateInsight = asyncActionCreators<UpdateInsightData, Insight[]>(
	"[INSIGHTS] UPDATE_INSIGHT",
	async (action, dispatch, getState) => {
		const { id, data, survey, onSuccess, onError } = action.payload;

		const state = getState();
		const uid = UserSelectors.selectLoggedInUserId(state);

		const updatedInsightsList =
			InsightsSelectors.selectInsights(state).value?.map((insight: any) =>
				insight.id === id ? { ...insight, ...data } : insight,
			) || [];
		dispatch(getInsights.resolved(updatedInsightsList));

		const endpoint = survey
			? Api.postInsightUpdateSurvey
			: Api.postInsightUserStatusUpdate;

		await endpoint
			.submit({
				params: { insightId: id, userId: uid || undefined },
				body: data,
			})
			.then((data) => {
				if (typeof onSuccess === "function") {
					onSuccess?.call(null);
				}
				return data;
			})
			.catch((error) => {
				if (typeof onError === "function") {
					onError?.call(null);
				}
				throw error;
			});
		Api.API_DEF.clearCache(/(insights)/);

		return updatedInsightsList;
	},
);

export const getInsights = asyncActionCreators<
	ScopeInfo & { anonymousMode?: boolean; locale?: string },
	Insight[]
>("[INSIGHTS] GET_INSIGHTS", async (action, dispatch, getState) => {
	const uid = UserSelectors.selectLoggedInUserId(getState());
	const localeToLoad =
		action.payload.locale ||
		StorageUtils.get(CURRENT_LOCALE_KEY, DEFAULT_LOCALE)?.code ||
		DEFAULT_LOCALE.code;

	if (!uid) {
		throw new Error("Missing user ID");
	}

	const query = { ...action.payload, locale: localeToLoad };

	if (!query.group) {
		query.group = undefined;
	}

	const result = await Api.fetchUserInsights.submit({
		query,
		params: { userId: uid },
		options: { cache: !action.payload.anonymousMode },
	});

	return result.data;
});
